import React from "react";
import type { SVGProps } from "react";

// Compact SVG provider logos for email list and account display.
// All icons render at the given size (default 20) and are self-contained.

type LogoProps = SVGProps<SVGSVGElement> & { size?: number };

function GmailLogo({ size = 20, ...props }: LogoProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} {...props}>
      <path fill="#4285F4" d="M2 6l10 7 10-7v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6z" />
      <path fill="#EA4335" d="M22 6l-10 7L2 6V4a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v2z" />
      <path fill="#FBBC05" d="M2 6l10 7V4L4 2A2 2 0 0 0 2 4v2z" opacity="0.8" />
      <path fill="#34A853" d="M22 6l-10 7V4l8-2a2 2 0 0 1 2 2v2z" opacity="0.8" />
    </svg>
  );
}

function YahooLogo({ size = 20, ...props }: LogoProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} {...props}>
      <rect width="24" height="24" rx="4" fill="#6001D2" />
      <text x="12" y="17" textAnchor="middle" fill="white" fontFamily="Arial, sans-serif" fontWeight="bold" fontSize="15">Y!</text>
    </svg>
  );
}

function OutlookLogo({ size = 20, ...props }: LogoProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} {...props}>
      <rect width="24" height="24" rx="4" fill="#0078D4" />
      <path d="M5 7l7 4.5L19 7" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="5" y="7" width="14" height="11" rx="1.5" stroke="white" strokeWidth="1.5" fill="none" />
    </svg>
  );
}

function AppleLogo({ size = 20, ...props }: LogoProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} {...props}>
      <rect width="24" height="24" rx="4" fill="#A3AAAE" />
      <path
        fill="white"
        d="M16.52 12.88c-.02-2.12 1.73-3.14 1.81-3.19-1-1.45-2.54-1.65-3.08-1.67-1.3-.14-2.56.78-3.23.78-.68 0-1.7-.77-2.81-.75-1.43.02-2.77.85-3.5 2.14-1.51 2.62-.39 6.48 1.06 8.6.72 1.04 1.57 2.2 2.68 2.16 1.08-.04 1.49-.7 2.8-.7 1.3 0 1.67.7 2.79.67 1.16-.02 1.89-1.04 2.59-2.09.83-1.2 1.17-2.37 1.18-2.43-.03-.01-2.27-.87-2.29-3.52zM14.4 6.63c.58-.73.98-1.72.87-2.73-.84.04-1.9.58-2.5 1.28-.54.63-1.02 1.66-.9 2.63.95.07 1.93-.47 2.53-1.18z"
      />
    </svg>
  );
}

function AolLogo({ size = 20, ...props }: LogoProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} {...props}>
      <rect width="24" height="24" rx="4" fill="#226699" />
      <text x="12" y="16.5" textAnchor="middle" fill="white" fontFamily="Arial, sans-serif" fontWeight="bold" fontSize="11">AOL</text>
    </svg>
  );
}

function ProtonLogo({ size = 20, ...props }: LogoProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} {...props}>
      <rect width="24" height="24" rx="4" fill="#6D4AFF" />
      <path d="M12 4l7 4v5c0 4-3 6.5-7 8-4-1.5-7-4-7-8V8l7-4z" fill="none" stroke="white" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M12 10v4m-2-2h4" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function MobyNetLogo({ size = 20, ...props }: LogoProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} {...props}>
      <rect width="24" height="24" rx="4" fill="#0D9488" />
      {/* Simplified whale silhouette based on MobyNet logo */}
      <g transform="translate(3,4) scale(0.75)">
        <path
          fill="white"
          d="M20.5 8.5c-.5-1.5-2-3-4-3.5.5-1.5.3-2.5-.2-3-.5-.5-1.2-.3-1.8.2-.3.3-.5.6-.7 1C12.5 2.5 11 2.2 9.5 2.5 7 3 5 4.5 3.5 6.5 2 8.5 1.5 11 2 13c.3 1.2.8 2 1.5 2.8l.5.5c1.5 1.3 3.5 2 5.5 2.2h1c1.5 0 3-.3 4.2-1 1-.5 1.8-1.2 2.5-2 .5-.5.8-1.2 1-1.8.5-.8 1-1.5 1.5-2 .5-.5.8-1.2 1-1.8.1-.5 0-1-.2-1.4zM6 10.5c-.5 0-1-.5-1-1s.5-1 1-1 1 .5 1 1-.5 1-1 1z"
        />
        {/* Water spout */}
        <path
          fill="white"
          opacity="0.7"
          d="M15.5 2c.2-.8.5-1.5 1-2 0 .8-.2 1.5-.5 2-.2.3-.3.5-.5.5 0-.2 0-.3 0-.5z"
        />
      </g>
    </svg>
  );
}

function GenericEmailLogo({ size = 20, ...props }: LogoProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} {...props}>
      <rect width="24" height="24" rx="4" fill="#78909C" />
      <path d="M5 8l7 4.5L19 8" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="5" y="7" width="14" height="10" rx="1.5" stroke="white" strokeWidth="1.5" fill="none" />
    </svg>
  );
}

// Provider logo registry — maps domain keywords to SVG components
const PROVIDER_LOGO_MAP: { test: (domain: string) => boolean; Logo: (props: LogoProps) => React.ReactNode; label: string }[] = [
  { test: d => d.includes("gmail") || d.includes("googlemail") || d.includes("google"), Logo: GmailLogo, label: "Google" },
  { test: d => d.includes("yahoo") || d.includes("ymail"), Logo: YahooLogo, label: "Yahoo" },
  { test: d => d.includes("outlook") || d.includes("hotmail") || d.includes("live.com") || d.includes("office365"), Logo: OutlookLogo, label: "Outlook" },
  { test: d => d.includes("icloud") || d === "me.com", Logo: AppleLogo, label: "Apple" },
  { test: d => d.includes("aol"), Logo: AolLogo, label: "AOL" },
  { test: d => d.includes("proton"), Logo: ProtonLogo, label: "Proton" },
  { test: d => d.includes("mobynet"), Logo: MobyNetLogo, label: "MobyNet" },
];

/**
 * Get the SVG logo component and label for an email address.
 * Extracts domain from "Name <addr>" or plain "addr" format.
 */
export function getProviderLogo(email: string): { Logo: (props: LogoProps) => React.ReactNode; label: string } {
  const match = email.match(/@([^>\s]+)/);
  const domain = match?.[1]?.toLowerCase() || "";
  for (const entry of PROVIDER_LOGO_MAP) {
    if (entry.test(domain)) return { Logo: entry.Logo, label: entry.label };
  }
  return { Logo: GenericEmailLogo, label: domain || "Email" };
}
